<div class="page">
  <app-page-header [title]="'compare.title' | translate" [subtitle]="'compare.subtitle' | translate">
    <a routerLink="/rankings" class="btn btn-secondary">
      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
      </svg>
      {{ 'compare.back' | translate }}
    </a>
  </app-page-header>

  <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 relative">
    <!-- VS Badge in the middle for Desktop -->
    <div class="hidden lg:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
      <div class="w-16 h-16 rounded-full accent-solid flex items-center justify-center font-black text-2xl border-4 border-slate-50 dark:border-slate-950 italic">
        VS
      </div>
    </div>

    <!-- Athlete 1 SLOT -->
    <div class="space-y-6">
      <div class="relative group">
        @if (isLoading1()) {
          <div class="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-slate-200 dark:border-slate-800 animate-pulse h-[300px]"></div>
        } @else if (human1(); as h1) {
          <div class="card relative overflow-hidden p-8">
            <button (click)="human1.set(null)" class="absolute top-4 right-4 p-2 text-slate-400 hover:text-red-500 transition-colors bg-slate-50 dark:bg-slate-800 rounded-xl">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div class="flex items-center gap-6">
              <div class="relative">
                <div class="w-24 h-24 rounded-2xl overflow-hidden border-4 border-slate-100 dark:border-slate-800 shadow-inner">
                  <app-human-avatar [imageUrl]="h1.imageUrl" [gender]="h1.gender" [name]="h1.name" class="h-full w-full" />
                </div>
                <div class="absolute -bottom-2 -right-2 bg-white dark:bg-slate-800 rounded-lg p-1.5 shadow-md border border-slate-100 dark:border-slate-700 text-xl leading-none">
                  {{ getFlagEmoji(h1.nationality) }}
                </div>
              </div>
              <div class="min-w-0">
                <h2 class="break-words text-2xl font-black text-slate-900 dark:text-white">{{ h1.name }} <app-registered-star [humanId]="h1.id" /></h2>
                <div class="flex flex-wrap items-center gap-2 mt-1">
                  <span class="accent-soft px-2 py-0.5 rounded-md text-xs font-black uppercase tracking-wider">
                    {{ h1.club || ('compare.independent' | translate) }}
                  </span>
                  @if (h1.age) {
                    <span class="text-slate-400 text-xs font-bold">{{ 'compare.age' | translate:h1.age }}</span>
                  }
                </div>
              </div>
            </div>
          </div>
        } @else {
          <!-- Empty Slot 1 -->
          <div (click)="openSearch(1)" class="bg-slate-200/50 dark:bg-slate-900/50 rounded-3xl p-12 border-4 border-dashed border-slate-300 dark:border-slate-800 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-200/80 dark:hover:bg-slate-800/80 transition-all group min-h-[220px]">
            <div class="w-16 h-16 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-[color:var(--accent-logo)] transition-colors shadow-sm mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <p class="text-slate-500 font-black uppercase tracking-widest text-sm">{{ 'compare.chooseAthlete' | translate }}</p>
          </div>
        }
      </div>

      <!-- Stats 1 -->
      @if (human1()) {
        <div class="grid grid-cols-2 gap-4">
          <div class="card rounded-2xl p-6">
            <span class="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">{{ 'compare.courses' | translate }}</span>
            <span class="text-2xl font-black text-slate-900 dark:text-white">{{ stats1().totalRaces }}</span>
          </div>
          <div class="card rounded-2xl p-6">
            <span class="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">{{ 'compare.totalDistance' | translate }}</span>
            <span class="text-2xl font-black text-slate-900 dark:text-white">{{ stats1().totalDistance | distance }} <small class="text-xs">KM</small></span>
          </div>
          <div class="accent-surface p-6 rounded-2xl border">
            <span class="text-[10px] font-black uppercase tracking-widest block mb-1">{{ 'compare.averagePosition' | translate }}</span>
            <span class="text-2xl font-black text-slate-900 dark:text-white">
              @if (stats1().averagePosition !== null) { #{{ stats1().averagePosition!.toFixed(1) }} } @else { {{ null | displayValue }} }
            </span>
          </div>
          <div class="bg-emerald-50/70 dark:bg-emerald-950/20 p-6 rounded-2xl border border-emerald-200 dark:border-emerald-800/60">
            <span class="text-[10px] font-black uppercase tracking-widest text-emerald-600 block mb-1">{{ 'compare.averageSpeed' | translate }}</span>
            <span class="text-2xl font-black text-emerald-700 dark:text-emerald-300">
              @if (stats1().averageSpeed !== null) { {{ stats1().averageSpeed!.toFixed(1) }} <small class="text-xs">KM/H</small> } @else { {{ null | displayValue }} }
            </span>
          </div>
          <div class="card rounded-2xl p-6" [class.border-accent]="stats1().bestRank === 1">
            <span class="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">{{ 'compare.bestRank' | translate }}</span>
            <span class="text-2xl font-black" [class.text-accent]="stats1().bestRank === 1">
              @if (stats1().bestRank) { #{{ stats1().bestRank }} } @else { {{ null | displayValue }} }
            </span>
          </div>
          <div class="card rounded-2xl p-6">
            <span class="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">{{ 'compare.maxSpeed' | translate }}</span>
            <span class="text-2xl font-black text-slate-900 dark:text-white">
              @if (stats1().maxSpeed) { {{ stats1().maxSpeed }} <small class="text-xs">KM/H</small> } @else { {{ null | displayValue }} }
            </span>
          </div>
        </div>

        <div class="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800">
          <h3 class="text-sm font-black uppercase tracking-widest text-slate-400 mb-6">{{ 'compare.palmares' | translate }}</h3>
          <div class="flex justify-around items-center">
            <div class="text-center">
               <div class="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-yellow-900 font-bold shadow-lg shadow-yellow-500/30 mx-auto mb-2 text-xl">1</div>
               <span class="text-lg font-black">{{ stats1().podiums.first }}</span>
            </div>
            <div class="text-center">
               <div class="w-12 h-12 bg-slate-300 rounded-full flex items-center justify-center text-slate-700 font-bold shadow-lg shadow-slate-400/30 mx-auto mb-2 text-xl">2</div>
               <span class="text-lg font-black">{{ stats1().podiums.second }}</span>
            </div>
            <div class="text-center">
               <div class="w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center text-orange-900 font-bold shadow-lg shadow-orange-500/30 mx-auto mb-2 text-xl">3</div>
               <span class="text-lg font-black">{{ stats1().podiums.third }}</span>
            </div>
          </div>
        </div>
      }
    </div>

    <!-- Athlete 2 SLOT -->
    <div class="space-y-6">
      <div class="relative group">
        @if (isLoading2()) {
          <div class="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-slate-200 dark:border-slate-800 animate-pulse h-[300px]"></div>
        } @else if (human2(); as h2) {
          <div class="card relative overflow-hidden p-8">
            <button (click)="human2.set(null)" class="absolute top-4 right-4 p-2 text-slate-400 hover:text-red-500 transition-colors bg-slate-50 dark:bg-slate-800 rounded-xl">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div class="flex flex-row-reverse md:flex-row items-center gap-6 text-right md:text-left">
              <div class="relative">
                <div class="w-24 h-24 rounded-2xl overflow-hidden border-4 border-slate-100 dark:border-slate-800 shadow-inner">
                   <app-human-avatar [imageUrl]="h2.imageUrl" [gender]="h2.gender" [name]="h2.name" class="h-full w-full" />
                </div>
                <div class="absolute -bottom-2 -left-2 bg-white dark:bg-slate-800 rounded-lg p-1.5 shadow-md border border-slate-100 dark:border-slate-700 text-xl leading-none">
                  {{ getFlagEmoji(h2.nationality) }}
                </div>
              </div>
              <div class="min-w-0 flex-1">
                <h2 class="break-words text-2xl font-black text-slate-900 dark:text-white">{{ h2.name }} <app-registered-star [humanId]="h2.id" /></h2>
                <div class="flex flex-wrap flex-row-reverse md:flex-row items-center gap-2 mt-1">
                  <span class="accent-soft px-2 py-0.5 rounded-md text-xs font-black uppercase tracking-wider">
                    {{ h2.club || ('compare.independent' | translate) }}
                  </span>
                  @if (h2.age) {
                    <span class="text-slate-400 text-xs font-bold">{{ 'compare.age' | translate:h2.age }}</span>
                  }
                </div>
              </div>
            </div>
          </div>
        } @else {
          <!-- Empty Slot 2 -->
          <div (click)="openSearch(2)" class="bg-slate-200/50 dark:bg-slate-900/50 rounded-3xl p-12 border-4 border-dashed border-slate-300 dark:border-slate-800 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-200/80 dark:hover:bg-slate-800/80 transition-all group min-h-[220px]">
            <div class="w-16 h-16 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-[color:var(--accent-logo)] transition-colors shadow-sm mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <p class="text-slate-500 font-black uppercase tracking-widest text-sm">{{ 'compare.chooseAthlete' | translate }}</p>
          </div>
        }
      </div>

      <!-- Stats 2 -->
      @if (human2()) {
        <div class="grid grid-cols-2 gap-4">
          <div class="card rounded-2xl p-6">
            <span class="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">{{ 'compare.courses' | translate }}</span>
            <span class="text-2xl font-black text-slate-900 dark:text-white">{{ stats2().totalRaces }}</span>
          </div>
          <div class="card rounded-2xl p-6">
            <span class="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">{{ 'compare.totalDistance' | translate }}</span>
            <span class="text-2xl font-black text-slate-900 dark:text-white">{{ stats2().totalDistance | distance }} <small class="text-xs">KM</small></span>
          </div>
          <div class="accent-surface p-6 rounded-2xl border">
            <span class="text-[10px] font-black uppercase tracking-widest block mb-1">{{ 'compare.averagePosition' | translate }}</span>
            <span class="text-2xl font-black text-slate-900 dark:text-white">
              @if (stats2().averagePosition !== null) { #{{ stats2().averagePosition!.toFixed(1) }} } @else { {{ null | displayValue }} }
            </span>
          </div>
          <div class="bg-emerald-50/70 dark:bg-emerald-950/20 p-6 rounded-2xl border border-emerald-200 dark:border-emerald-800/60">
            <span class="text-[10px] font-black uppercase tracking-widest text-emerald-600 block mb-1">{{ 'compare.averageSpeed' | translate }}</span>
            <span class="text-2xl font-black text-emerald-700 dark:text-emerald-300">
              @if (stats2().averageSpeed !== null) { {{ stats2().averageSpeed!.toFixed(1) }} <small class="text-xs">KM/H</small> } @else { {{ null | displayValue }} }
            </span>
          </div>
          <div class="card rounded-2xl p-6" [class.border-accent]="stats2().bestRank === 1">
            <span class="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">{{ 'compare.bestRank' | translate }}</span>
            <span class="text-2xl font-black" [class.text-accent]="stats2().bestRank === 1">
              @if (stats2().bestRank) { #{{ stats2().bestRank }} } @else { {{ null | displayValue }} }
            </span>
          </div>
          <div class="card rounded-2xl p-6">
            <span class="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">{{ 'compare.maxSpeed' | translate }}</span>
            <span class="text-2xl font-black text-slate-900 dark:text-white">
              @if (stats2().maxSpeed) { {{ stats2().maxSpeed }} <small class="text-xs">KM/H</small> } @else { {{ null | displayValue }} }
            </span>
          </div>
        </div>

        <div class="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800">
          <h3 class="text-sm font-black uppercase tracking-widest text-slate-400 mb-6">{{ 'compare.palmares' | translate }}</h3>
          <div class="flex justify-around items-center text-right md:text-center">
            <div class="text-center">
               <div class="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-yellow-900 font-bold shadow-lg shadow-yellow-500/30 mx-auto mb-2 text-xl">1</div>
               <span class="text-lg font-black">{{ stats2().podiums.first }}</span>
            </div>
            <div class="text-center">
               <div class="w-12 h-12 bg-slate-300 rounded-full flex items-center justify-center text-slate-700 font-bold shadow-lg shadow-slate-400/30 mx-auto mb-2 text-xl">2</div>
               <span class="text-lg font-black">{{ stats2().podiums.second }}</span>
            </div>
            <div class="text-center">
               <div class="w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center text-orange-900 font-bold shadow-lg shadow-orange-500/30 mx-auto mb-2 text-xl">3</div>
               <span class="text-lg font-black">{{ stats2().podiums.third }}</span>
            </div>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Face to Face Section -->
  @if (human1() && human2()) {
    <div class="mt-16">
      <div class="bg-slate-900 rounded-3xl p-8 md:p-12 relative overflow-hidden">
        <!-- Background decorative elements -->
        <div class="absolute top-0 right-0 w-64 h-64 bg-[color-mix(in_srgb,var(--region-accent)_22%,transparent)] rounded-full blur-3xl -mr-32 -mt-32"></div>
        <div class="absolute bottom-0 left-0 w-64 h-64 bg-emerald-600/10 rounded-full blur-3xl -ml-32 -mb-32"></div>

        <div class="relative z-10">
          <div class="flex items-center justify-between mb-12">
            <div>
              <h3 class="text-3xl font-black text-white uppercase italic tracking-tighter">{{ 'compare.headToHead' | translate }}</h3>
              <p class="text-slate-500 font-bold text-sm tracking-widest uppercase mt-1">{{ 'compare.directDuels' | translate }}</p>
            </div>
            <div class="flex items-center gap-6 bg-slate-800/50 backdrop-blur-md rounded-2xl px-8 py-4 border border-slate-700">
               <div class="text-center">
                 <span class="text-xs font-black text-slate-500 block mb-1 uppercase">{{ human1()!.name.split(' ')[0] }}</span>
                 <span class="text-3xl font-black text-white italic">{{ h2hScore().score1 }}</span>
               </div>
               <div class="text-2xl font-black text-slate-700 italic"> - </div>
               <div class="text-center">
                 <span class="text-xs font-black text-slate-500 block mb-1 uppercase">{{ human2()!.name.split(' ')[0] }}</span>
                 <span class="text-3xl font-black text-white italic">{{ h2hScore().score2 }}</span>
               </div>
            </div>
          </div>

          @if (faceToFace().length > 0) {
            <div class="space-y-4">
              @for (match of faceToFace(); track match.race?.id) {
                <div class="bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 transition-all group">
                  <div class="grid grid-cols-1 md:grid-cols-4 items-center gap-6">
                    <div class="md:col-span-1">
                      <span class="text-[10px] font-black uppercase text-accent block mb-1">{{ formatMonth(match.event?.date) }}</span>
                      <h4 class="text-white font-bold">{{ match.race.name }}</h4>
                      <p class="text-xs text-slate-500">{{ match.event?.location | displayValue }}</p>
                    </div>
                    
                    <div class="flex items-center justify-center gap-8 md:col-span-2">
                       <div class="flex items-center gap-4" [class.opacity-30]="match.winner === 2">
                          <div class="text-right">
                             <div class="text-xs font-black text-white italic">#{{ match.rank1 }}</div>
                             <div class="text-[10px] font-mono text-slate-500">{{ formatTime(match.time1) }}</div>
                          </div>
                          @if (match.winner === 1) {
                            <div class="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                              </svg>
                            </div>
                          }
                       </div>
                       
                       <div class="w-px h-8 bg-slate-700"></div>

                       <div class="flex items-center gap-4" [class.opacity-30]="match.winner === 1">
                          @if (match.winner === 2) {
                            <div class="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                              </svg>
                            </div>
                          }
                          <div class="text-left">
                             <div class="text-xs font-black text-white italic">#{{ match.rank2 }}</div>
                             <div class="text-[10px] font-mono text-slate-500">{{ formatTime(match.time2) }}</div>
                          </div>
                       </div>
                    </div>

                    <div class="text-center md:text-right">
                      @if (match.winner !== 0) {
                         <span class="text-[10px] font-black uppercase tracking-tighter px-3 py-1 rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/20">
                          {{ 'compare.winner' | translate:(match.winner === 1 ? human1()!.name.split(' ')[0] : human2()!.name.split(' ')[0]) }}
                        </span>
                      } @else {
                         <span class="text-[10px] font-black uppercase tracking-tighter px-3 py-1 rounded-full bg-slate-700 text-slate-400">{{ 'compare.tie' | translate }}</span>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          } @else {
            <div class="text-center py-20 bg-slate-800/20 rounded-3xl border border-dashed border-slate-700">
               <div class="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center text-slate-600 mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
               </div>
               <p class="text-slate-500 font-bold">{{ 'compare.noCommonRaces' | translate }}</p>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Search UI (Floating above or fixed) -->
  @if (!human1() || !human2() || isSelectionMode() || showSearchOverlay()) {
     @if (searchQuery() !== null && ((activeSlot() === 1 && !human1()) || (activeSlot() === 2 && !human2()) || showSearchOverlay() || isSelectionMode())) {
       <div class="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 bg-slate-900/80 backdrop-blur-sm" (click)="closeSearch()">
           <div class="bg-white dark:bg-slate-950 w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden" (click)="$event.stopPropagation()">
              <div class="p-8">
                 <div class="flex items-center justify-between gap-4 mb-6">
                   <div>
                     <span class="text-[10px] font-black text-accent uppercase tracking-widest">{{ 'compare.athleteSlot' | translate:activeSlot() }}</span>
                     <h3 class="text-2xl font-black text-slate-900 dark:text-white uppercase italic">{{ 'compare.chooseOpponent' | translate }}</h3>
                   </div>
                   <button type="button" (click)="closeSearch()" class="p-3 rounded-xl bg-slate-100 dark:bg-slate-900 text-slate-500 hover:text-red-500" [attr.aria-label]="'compare.close' | translate">
                     <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                   </button>
                 </div>
                <div class="relative">
                   <input type="search" [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event); onSearch()" [placeholder]="'compare.searchPlaceholder' | translate"
                      [attr.aria-label]="'compare.searchPlaceholder' | translate" class="field py-4 pl-12 text-base">
                   <svg xmlns="http://www.w3.org/2000/svg" class="pointer-events-none h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                   </svg>
                </div>

                <div class="mt-8 space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                   @if (isSearching()) {
                     <div class="flex items-center justify-center py-10">
                        <div class="animate-spin rounded-full h-10 w-10 border-4 border-[color:var(--region-accent)] border-t-transparent"></div>
                     </div>
                   } @else if (searchResults().length > 0) {
                      @for (h of searchResults(); track h.id) {
                         <div (click)="selectProfile(h)" class="flex items-center gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-900 border border-transparent hover:border-slate-200 dark:hover:border-slate-800 rounded-2xl cursor-pointer transition-all group">
                            <div class="w-14 h-14 rounded-xl overflow-hidden shadow-md">
                                <app-human-avatar [imageUrl]="h.imageUrl" [gender]="h.gender" [name]="h.name" class="h-full w-full" />
                            </div>
                            <div>
                               <h4 class="font-black text-slate-900 dark:text-white group-hover:text-[color:var(--accent-logo)] transition-colors">{{ h.name }} <app-registered-star [humanId]="h.id" /></h4>
                               <span class="text-xs font-bold text-slate-400 uppercase tracking-widest">{{ h.club || ('compare.independent' | translate) }}</span>
                            </div>
                            <div class="flex-1"></div>
                            <div class="text-2xl">{{ getFlagEmoji(h.nationality) }}</div>
                         </div>
                      }
                   } @else if (searchQuery().length >= 2) {
                      <div class="text-center py-10">
                         <p class="text-slate-400 font-bold uppercase tracking-widest text-xs">{{ 'compare.noResults' | translate }}</p>
                      </div>
                   }
                </div>
             </div>
          </div>
       </div>
     }
  }
</div>
